import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AlertTriangle, Clock, XCircle, CheckCircle2, FileClock, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import LoadingState from "@/components/ui/loading-state";

const urgencyStyles = {
  expired: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800", chip: "bg-rose-100 text-rose-800", icon: XCircle, label: "Sudah Lewat" },
  critical: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-800", chip: "bg-orange-100 text-orange-800", icon: AlertTriangle, label: "≤ 7 hari" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", chip: "bg-amber-100 text-amber-800", icon: Clock, label: "≤ 14 hari" },
  info: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-800", chip: "bg-sky-100 text-sky-800", icon: FileClock, label: "≤ 30 hari" },
};

export default function Kontrak() {
  const { user } = useAuth();
  const canNotify = user?.role !== "viewer";
  const [items, setItems] = useState(null);
  const [days, setDays] = useState(30);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/kontrak/reminders?days=${days}`).then(r => setItems(r.data));
  }, [days]);

  const sendWA = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/kontrak/notify-wa", { days });
      if (data.sent) toast.success(`WhatsApp terkirim ke HR (${data.count} karyawan)`);
      else toast.info(data.note);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim"); }
    setSending(false);
  };

  const grouped = items ? items.reduce((acc, it) => {
    (acc[it.urgency] = acc[it.urgency] || []).push(it);
    return acc;
  }, {}) : {};

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Kontrak</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Reminder Kontrak PKWT</h1>
        <p className="mt-2 text-sm text-slate-500">Karyawan PKWT yang kontraknya segera habis. Update <span className="font-medium">Tgl Habis Kontrak</span> di data karyawan untuk akurasi maksimal — jika kosong, dihitung otomatis dari Tanggal Masuk + 12 bulan.</p>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-slate-600">Tampilkan dalam:</span>
        {[7, 14, 30, 60, 90].map(d => (
          <button key={d} onClick={() => setDays(d)} data-testid={`btn-days-${d}`}
            className={`h-8 px-3 rounded-lg text-xs font-medium transition-colors ${
              days === d ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}>{d} hari</button>
        ))}
        {canNotify && items && items.length > 0 && (
          <button onClick={sendWA} disabled={sending} data-testid="btn-send-wa"
            className="ml-auto inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-sm font-medium transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> {sending ? "Mengirim..." : "Kirim WA ke HR"}
          </button>
        )}
      </div>

      {items === null && <LoadingState loading data-testid="kontrak-loading" />}
      {items && items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="font-display font-semibold text-slate-900">Tidak ada kontrak yang akan habis dalam {days} hari</p>
          <p className="text-sm text-slate-500 mt-1">Semua karyawan PKWT aman.</p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="space-y-6">
          {["expired", "critical", "warning", "info"].map(k => {
            const list = grouped[k];
            if (!list?.length) return null;
            const s = urgencyStyles[k];
            const Icon = s.icon;
            return (
              <section key={k}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-4 h-4 ${s.text}`} />
                  <h3 className={`font-display font-semibold ${s.text}`}>{s.label}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.chip}`}>{list.length}</span>
                </div>
                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4`}>
                  {list.map(it => (
                    <div key={it.id} data-testid={`kontrak-${it.id}`}
                      className={`rounded-xl border ${s.border} ${s.bg} p-5`}>
                      <p className={`text-xs font-semibold uppercase tracking-wider ${s.text} mb-2`}>
                        {it.days_left < 0 ? `Lewat ${Math.abs(it.days_left)} hari` : `Sisa ${it.days_left} hari`}
                      </p>
                      <p className="font-display font-semibold text-slate-900">{it.nama}</p>
                      <p className="text-xs font-mono text-slate-600 mt-0.5">{it.nik}</p>
                      <div className="mt-3 pt-3 border-t border-slate-200/60 text-xs space-y-1">
                        <div className="flex justify-between"><span className="text-slate-500">Branch</span><span className="text-slate-800">{it.branch}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Grade</span><span className="text-slate-800 text-right">{it.grade_jabatan}</span></div>
                        {/* Masa kerja (brief owner 2 Sep 2026): saat memutuskan
                            perpanjangan, "sudah berapa lama dia di sini" adalah
                            angka yang paling dicari. Datang sudah jadi dari
                            server — rumusnya satu, dipakai bersama Kartu
                            Karyawan dan tabel Data Karyawan. */}
                        <div className="flex justify-between" data-testid={`kontrak-masa-kerja-${it.id}`}>
                          <span className="text-slate-500">Masa Kerja</span>
                          <span className="text-slate-800">{it.masa_kerja || "-"}</span>
                        </div>
                        <div className="flex justify-between"><span className="text-slate-500">Habis</span><span className="text-slate-800 font-semibold">{it.tgl_habis_kontrak}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
